import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Row, Col, Spinner, Alert } from 'react-bootstrap';
import { useEntidadeContext } from '../../EntidadeContext';
import { useAlertModal } from '../../AlertModalContext';
import axios from 'axios';
import InputMask from 'react-input-mask';

const EntidadeTab = () => {
    const { currentEntidade, setCurrentEntidade } = useEntidadeContext();
    const { showAlertModal } = useAlertModal();

    // Estado para os dados do formulário
    const [formData, setFormData] = useState({
        nome: '',
        cnpj: '',
        email: '',
        telefone: '',
        whatsapp_funil: '', // Novo campo para o WhatsApp do funil
        // Novos campos de endereço
        cep: '',
        endereco: '',
        numero: '',
        bairro: '',
        cidade: '',
        estado: '',
    });

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isChanged, setIsChanged] = useState(false);

    // Efeito para popular o formulário quando a entidade do contexto é carregada
    useEffect(() => {
        if (currentEntidade) {
            setFormData({
                nome: currentEntidade.nome || '',
                cnpj: currentEntidade.cnpj || '',
                email: currentEntidade.email || '',
                telefone: currentEntidade.telefone || '',
                whatsapp_funil: currentEntidade.whatsapp_funil || '',
                // Popula os novos campos de endereço
                cep: currentEntidade.cep || '',
                endereco: currentEntidade.endereco || '',
                numero: currentEntidade.numero || '',
                bairro: currentEntidade.bairro || '',
                cidade: currentEntidade.cidade || '',
                estado: currentEntidade.estado || '',
            });
            setIsChanged(false); // Reseta o estado de alteração
        }
    }, [currentEntidade]);
    
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setIsChanged(true); // Marca que houve alteração
    };

    const handleCepChange = async (e) => {
        const cep = e.target.value.replace(/\D/g, '');
        handleChange(e); // Atualiza o estado do CEP

        if (cep.length === 8) {
            try {
                const response = await axios.get(`https://viacep.com.br/ws/${cep}/json/`);
                const data = response.data;
                if (!data.erro) {
                    setFormData(prev => ({
                        ...prev,
                        endereco: data.logradouro,
                        bairro: data.bairro,
                        cidade: data.localidade,
                        estado: data.uf,
                    }));
                }
            } catch (error) {
                console.error("Erro ao buscar CEP:", error);
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!currentEntidade) {
            setError('Nenhuma entidade selecionada.');
            return;
        }

        setIsLoading(true);
        setError(null);
        
        try {
            // BACKEND: Este endpoint precisará ser criado ou ajustado para receber estes campos.
            const response = await axios.put(`/api/entidades/${currentEntidade.id}`, formData);

            // Atualiza o contexto da entidade com os novos dados (incluindo a nova logo_url)
            setCurrentEntidade(response.data.entidade);
            showAlertModal('Sucesso', 'Dados da entidade atualizados com sucesso!', 'success');
            setIsChanged(false); // Reseta o estado de alteração após salvar

        } catch (err) {
            const apiError = err.response?.data?.message || 'Ocorreu um erro ao atualizar os dados.';
            setError(apiError);
            showAlertModal('Erro', apiError, 'danger');
        } finally {
            setIsLoading(false);
        }
    };
    
    return (
        <Card>
            <Card.Header>
                <Card.Title>Dados Gerais da Entidade</Card.Title>
            </Card.Header>
            <Card.Body>
                <Form onSubmit={handleSubmit}>
                    <Row>
                        <Col md={6}><Form.Group className="mb-3"><Form.Label>Nome da Clínica</Form.Label><Form.Control type="text" name="nome" value={formData.nome} onChange={handleChange} readOnly={!!currentEntidade?.nome} /></Form.Group></Col>
                        <Col md={6}><Form.Group className="mb-3"><Form.Label>CNPJ</Form.Label><InputMask mask="99.999.999/9999-99" value={formData.cnpj} onChange={handleChange} readOnly={!!currentEntidade?.cnpj}>{(inputProps) => <Form.Control {...inputProps} name="cnpj" readOnly={!!currentEntidade?.cnpj} />}</InputMask></Form.Group></Col>
                    </Row>
                    <Row>
                        <Col md={6}><Form.Group className="mb-3"><Form.Label>E-mail Principal</Form.Label><Form.Control type="email" name="email" value={formData.email} onChange={handleChange} readOnly={!!currentEntidade?.email} /></Form.Group></Col>
                        <Col md={6}><Form.Group className="mb-3"><Form.Label>Telefone Principal</Form.Label><InputMask mask="(99) 9999-9999" value={formData.telefone} onChange={handleChange} readOnly={!!currentEntidade?.telefone}>{(inputProps) => <Form.Control {...inputProps} name="telefone" readOnly={!!currentEntidade?.telefone} />}</InputMask></Form.Group></Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>WhatsApp (Funil de Vendas)</Form.Label>
                                <InputMask mask="(99) 99999-9999" value={formData.whatsapp_funil} onChange={handleChange}>{(inputProps) => <Form.Control {...inputProps} name="whatsapp_funil" />}</InputMask> {/* Este campo permanece editável */}
                                <Form.Text muted>Este número será usado para redirecionar leads das páginas de captura.</Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>
                    <hr />
                    <h5 className="mb-3">Endereço Principal</h5>
                    <Row>
                        <Col md={4}><Form.Group className="mb-3"><Form.Label>CEP</Form.Label><InputMask mask="99999-999" value={formData.cep} onChange={handleCepChange} readOnly={!!currentEntidade?.cep}>{(inputProps) => <Form.Control {...inputProps} name="cep" readOnly={!!currentEntidade?.cep} />}</InputMask></Form.Group></Col>
                        <Col md={8}><Form.Group className="mb-3"><Form.Label>Endereço</Form.Label><Form.Control type="text" name="endereco" value={formData.endereco} onChange={handleChange} readOnly={!!currentEntidade?.endereco} /></Form.Group></Col>
                    </Row>
                    <Row>
                        <Col md={4}><Form.Group className="mb-3"><Form.Label>Número</Form.Label><Form.Control type="text" name="numero" value={formData.numero} onChange={handleChange} readOnly={!!currentEntidade?.numero} /></Form.Group></Col>
                        <Col md={8}><Form.Group className="mb-3"><Form.Label>Bairro</Form.Label><Form.Control type="text" name="bairro" value={formData.bairro} onChange={handleChange} readOnly={!!currentEntidade?.bairro} /></Form.Group></Col>
                    </Row>
                    <Row>
                        <Col md={8}><Form.Group className="mb-3"><Form.Label>Cidade</Form.Label><Form.Control type="text" name="cidade" value={formData.cidade} onChange={handleChange} readOnly={!!currentEntidade?.cidade} /></Form.Group></Col>
                        <Col md={4}><Form.Group className="mb-3"><Form.Label>Estado (UF)</Form.Label><Form.Control type="text" name="estado" value={formData.estado} onChange={handleChange} maxLength="2" readOnly={!!currentEntidade?.estado} /></Form.Group></Col>
                    </Row>
                    {error && <Alert variant="danger">{error}</Alert>}
                    <div className="d-flex justify-content-end">
                        <Button variant="primary" type="submit" disabled={isLoading || !isChanged}>
                            {isLoading ? <><Spinner as="span" animation="border" size="sm" /> Salvando...</> : 'Salvar Alterações'}
                        </Button>
                    </div>
                </Form>
            </Card.Body>
        </Card>
    );
};

export default EntidadeTab;